"use client"

import { clx } from "@modules/common/components/ui"
import { useEffect, useRef } from "react"

type RevealProps = {
  children: React.ReactNode
  /** Stagger offset in milliseconds, applied as a transition-delay */
  delay?: number
  className?: string
}

/**
 * Scroll-reveal wrapper: fades content up once it enters the viewport.
 * Pure CSS transition driven by an IntersectionObserver-added class.
 * Reduced-motion users see content immediately (see globals.css).
 */
export default function Reveal({ children, delay = 0, className }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const node = ref.current
    if (!node) {
      return
    }

    if (typeof IntersectionObserver === "undefined") {
      node.classList.add("is-revealed")
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed")
            observer.unobserve(entry.target)
          }
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={clx("reveal", className)}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  )
}
